import type { AcousticDecisionProfile } from '../../lib/liveLessonTypes';

type Row = { label: string; value: string };

function fmt(value: number, digits = 2): string {
  return Number.isFinite(value) ? value.toFixed(digits) : '—';
}

function boolLabel(value: boolean): string {
  return value ? 'ja' : 'nein';
}

type CalibrationValueTableProps = {
  decisionProfile?: AcousticDecisionProfile;
};

export default function CalibrationValueTable({ decisionProfile }: CalibrationValueTableProps) {
  if (!decisionProfile) {
    return (
      <p className="text-xs text-gray-500 dark:text-gray-400">
        Entscheidungswerte erscheinen, sobald die Audio-Pipeline läuft.
      </p>
    );
  }

  const rows: Row[] = [
    { label: 'YAMNet Sprache', value: fmt(decisionProfile.yamnet.speech) },
    { label: 'YAMNet Dialog', value: fmt(decisionProfile.yamnet.conversation) },
    { label: 'YAMNet Gemurmel', value: fmt(decisionProfile.yamnet.babble) },
    { label: 'YAMNet Geschrei', value: fmt(decisionProfile.yamnet.shouting) },
    { label: 'Fusion Lautstärke', value: fmt(decisionProfile.fusion.loudness) },
    { label: 'Fusion Rauschen', value: fmt(decisionProfile.fusion.noiseIntensity) },
    { label: 'Fusion Wechselsprech.', value: fmt(decisionProfile.fusion.turnTaking) },
    { label: 'Fusion Diffusheit', value: fmt(decisionProfile.fusion.diffuseness) },
    { label: 'Fusion Kohärenz', value: fmt(decisionProfile.fusion.speechCoherence) },
    { label: 'Fusion Crest-Faktor', value: fmt(decisionProfile.fusion.crestFactor) },
    { label: 'Fusion Peak-Dichte', value: fmt(decisionProfile.fusion.peakDensity, 3) },
    { label: 'Sprache aktiv', value: boolLabel(decisionProfile.fusion.effectiveHasSpeech) },
    { label: 'Frame Einzelsprecher', value: fmt(decisionProfile.frameFusionScores.single_speaker) },
    { label: 'Frame Dialog', value: fmt(decisionProfile.frameFusionScores.conversation) },
    { label: 'Frame Gemurmel', value: fmt(decisionProfile.frameFusionScores.babble) },
    { label: 'Frame Geschrei', value: fmt(decisionProfile.frameFusionScores.shouting) },
    { label: 'Entscheidung Einzel (3s)', value: fmt(decisionProfile.integratedScores.single_speaker) },
    { label: 'Entscheidung Dialog (3s)', value: fmt(decisionProfile.integratedScores.conversation) },
    { label: 'Entscheidung Gemurmel (3s)', value: fmt(decisionProfile.integratedScores.babble) },
    { label: 'Entscheidung Geschrei (3s)', value: fmt(decisionProfile.integratedScores.shouting) },
    { label: 'Modus Klassen-Gemurmel', value: boolLabel(decisionProfile.classroomModes.classroomBabble) },
    { label: 'Modus Klassen-Geschrei', value: boolLabel(decisionProfile.classroomModes.classroomShouting) }
  ];

  return (
    <div className="max-h-52 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-700">
      <table className="w-full text-xs">
        <tbody>
          {rows.map((row) => (
            <tr key={row.label} className="border-b border-gray-100 dark:border-gray-800">
              <td className="px-2 py-1.5 text-gray-500 dark:text-gray-400">{row.label}</td>
              <td className="px-2 py-1.5 text-right font-mono font-medium text-gray-800 dark:text-gray-200">
                {row.value}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
